import { useCallback, type FormEvent } from 'react'
import { useUpdateAgencyMutation } from '@/modules/agencies/api/agenciesApi'
import type { Agency } from '@/modules/agencies/types/agency'
import { slugifyAgencyName } from '@/modules/agencies/utils/slugifyAgencyName'
import { validateAgencyCreate } from '@/modules/agencies/utils/validateAgencyCreate'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  selectAgencyFormFieldErrors,
  selectAgencyFormFields,
  selectAgencyFormSubmitError,
  selectEditingAgencyId,
  selectIsAgencyFormSubmitting,
  selectIsEditAgencyOpen,
} from '@/store/selectors/agenciesUiSelectors'
import {
  agencyFormFailed,
  agencyFormFieldChanged,
  agencyFormSlugSynced,
  agencyFormSubmitted,
  agencyFormSucceeded,
  agencyFormValidationFailed,
  editAgencyClosed,
  editAgencyOpened,
} from '@/store/slices/agenciesUiSlice'
import { getAuthErrorMessage } from '@/utils/getAuthErrorMessage'

export function useAgencyEditForm() {
  const dispatch = useAppDispatch()
  const isOpen = useAppSelector(selectIsEditAgencyOpen)
  const editingAgencyId = useAppSelector(selectEditingAgencyId)
  const fields = useAppSelector(selectAgencyFormFields)
  const fieldErrors = useAppSelector(selectAgencyFormFieldErrors)
  const submitError = useAppSelector(selectAgencyFormSubmitError)
  const isSubmitting = useAppSelector(selectIsAgencyFormSubmitting)
  const [updateAgency] = useUpdateAgencyMutation()

  const openEdit = useCallback(
    (agency: Agency) => {
      dispatch(
        editAgencyOpened({
          agencyId: agency.id,
          name: agency.name,
          slug: agency.slug,
        }),
      )
    },
    [dispatch],
  )

  const closeEdit = useCallback(() => {
    if (isSubmitting) {
      return
    }
    dispatch(editAgencyClosed())
  }, [dispatch, isSubmitting])

  const onNameChange = useCallback(
    (value: string) => {
      dispatch(agencyFormFieldChanged({ field: 'name', value }))
      dispatch(agencyFormSlugSynced(slugifyAgencyName(value)))
    },
    [dispatch],
  )

  const onSlugChange = useCallback(
    (value: string) => {
      dispatch(agencyFormFieldChanged({ field: 'slug', value }))
    },
    [dispatch],
  )

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      if (!editingAgencyId) {
        dispatch(agencyFormFailed('Missing agency id.'))
        return
      }

      const validation = validateAgencyCreate(fields)
      if (!validation.ok) {
        dispatch(agencyFormValidationFailed(validation.fieldErrors))
        return
      }

      dispatch(agencyFormSubmitted())
      const result = await updateAgency({
        agencyId: editingAgencyId,
        body: validation.value,
      })

      if (result.error) {
        dispatch(agencyFormFailed(getAuthErrorMessage(result.error)))
        return
      }

      dispatch(agencyFormSucceeded())
    },
    [dispatch, editingAgencyId, fields, updateAgency],
  )

  return {
    isOpen,
    name: fields.name,
    slug: fields.slug,
    fieldErrors,
    submitError,
    isSubmitting,
    openEdit,
    closeEdit,
    onNameChange,
    onSlugChange,
    onSubmit,
  }
}
